import React, { useCallback, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Slider, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_PRO_BRAND } from '@gql/pros';

interface CompanyBannerProps {
    pro?: {
        _id?: string;
        publicProfile?: string;
        commercialName?: string;
        numberOfEmployees?: number;
        city?: string;
        logo?: string;
        photo?: string;
        totalJobOffers?: number;
        totalJobApplications?: number;
        totalBrands?: number;
        social?: {
            website?: string;
        };
        principalProfession?: {
            name?: string;
        };
        sector?: {
            sectorName?: string;
        };
        brandLogo?: {
            _id?: string;
            url?: string;
        };
        brandBanner?: {
            _id?: string;
            url?: string;
        };
        brandVideos?: Array<{
            _id?: string;
            url?: string;
        }>;
        brandPhotos?: Array<{
            _id?: string;
            url?: string;
        }>;
        salaryStats?: {
            lowest?: number | null;
            highest?: number | null;
        } | null;
    };
    editable?: boolean;
}

const formatWebsite = (website?: string) => website?.replace(/^(http|https):\/\//, '').replace(/\/$/, '');

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (err) => reject(err));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not create canvas context');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
    return new Promise((resolve) => {
        canvas.toBlob((blob) => { if (blob) resolve(blob); }, 'image/jpeg', 0.95);
    });
};

type CropTarget = 'banner' | 'logo';

const CompanyBanner = ({ pro, editable = false }: CompanyBannerProps) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [bannerHovered, setBannerHovered] = useState(false);
    const [logoHovered, setLogoHovered] = useState(false);

    const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const [bannerPreview, setBannerPreview] = useState<string | undefined>(pro?.brandBanner?.url);
    const [logoPreview, setLogoPreview] = useState<string | undefined>(pro?.brandLogo?.url || pro?.logo || pro?.photo);

    const bannerInputRef = useRef<HTMLInputElement>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);

    const [updateProBrand, { loading: uploading }] = useMutation(UPDATE_PRO_BRAND, {
        onCompleted: (data) => {
            const updated = data?.updateProBrand;
            if (updated?.brandBanner?.url) setBannerPreview(updated.brandBanner.url);
            if (updated?.brandLogo?.url) setLogoPreview(updated.brandLogo.url);
            enqueueSnackbar('Médias de marque mis à jour', { variant: 'success' });
        },
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: 'error' });
        },
    });

    const onCropComplete = useCallback((_: Area, cropped: Area) => {
        setCroppedAreaPixels(cropped);
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, target: CropTarget) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setCropTarget(target);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        };
        reader.readAsDataURL(file);
    };

    const handleCropSave = async () => {
        if (!imageSrc || !croppedAreaPixels || !cropTarget || !pro?._id) return;
        try {
            const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const fileName = cropTarget === 'banner' ? 'banner.jpg' : 'logo.jpg';
            const file = new File([blob], fileName, { type: 'image/jpeg' });

            const brandInput = cropTarget === 'banner'
                ? {
                    brandBanner: { file },
                    brandLogo: pro?.brandLogo?._id ? { _id: pro.brandLogo._id } : undefined,
                    brandVideos: pro?.brandVideos?.map(video => video._id ? { _id: video._id } : null).filter(Boolean) || [],
                    brandPhotos: pro?.brandPhotos?.map(photo => photo._id ? { _id: photo._id } : null).filter(Boolean) || [],
                    ...(pro?.brandLogo?._id ? { brandLogo: { _id: pro.brandLogo._id } } : {}),
                }
                : {
                    brandLogo: { file },
                    brandBanner: pro?.brandBanner?._id ? { _id: pro.brandBanner._id } : undefined,
                    brandVideos: pro?.brandVideos?.map(video => video._id ? { _id: video._id } : null).filter(Boolean) || [],
                    brandPhotos: pro?.brandPhotos?.map(photo => photo._id ? { _id: photo._id } : null).filter(Boolean) || [],
                    ...(pro?.brandBanner?._id ? { brandBanner: { _id: pro.brandBanner._id } } : {}),
                };

            await updateProBrand({
                variables: { proId: pro._id, brandInput },
                context: { fetchOptions: { useUpload: true } },
                refetchQueries: [{ query: GET_PRO, variables: { proId: pro._id } }],
                awaitRefetchQueries: true,
            });
        } catch {
            enqueueSnackbar("Échec du recadrage de l'image", { variant: 'error' });
        } finally {
            setCropTarget(null);
            setImageSrc(null);
        }
    };

    const handleCropCancel = () => {
        setCropTarget(null);
        setImageSrc(null);
    };

    const companyName = pro?.commercialName?.trim() || 'Etablissement';
    const city = pro?.city?.trim();
    const category = pro?.sector?.sectorName?.trim();
    const website = formatWebsite(pro?.publicProfile);
    const avatarSrc = logoPreview || undefined;
    const bannerSrc = bannerPreview;

    return (
        <>
            {/* Hidden file inputs */}
            {editable && (
                <>
                    <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'banner')}
                    />
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'logo')}
                    />
                </>
            )}

            <Box
                onMouseEnter={() => editable && setBannerHovered(true)}
                onMouseLeave={() => editable && setBannerHovered(false)}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 190, md: 227 },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    p: { xs: 2, md: 3 },
                    backgroundColor: theme.palette.primary.main,
                    backgroundImage: bannerSrc
                        ? `
                            linear-gradient(100deg, ${alpha(theme.palette.neutralBlack.main, 0.72)} 0%, ${alpha(
                              theme.palette.neutralBlack.main,
                              0.28,
                            )} 45%, ${alpha(theme.palette.primary.main, 0.58)} 100%),
                            url(${bannerSrc})
                        `
                        : `
                            radial-gradient(circle at 15% 12%, ${alpha(theme.palette.warningOrange.main, 0.85)} 0%, transparent 36%),
                            radial-gradient(circle at 45% 8%, ${alpha(theme.palette.errorRed.main, 0.7)} 0%, transparent 42%),
                            radial-gradient(circle at 88% 82%, ${alpha(
                                theme.palette.transparentPurple?.main || theme.palette.primary.main,
                                0.9,
                            )} 0%, transparent 42%),
                            linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.errorRed.main} 56%, ${
                                theme.palette.neutralBlack.main
                            } 100%)
                        `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                {/* Banner edit overlay */}
                {editable && (
                    <Box
                        onClick={() => bannerInputRef.current?.click()}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.neutralBlack.main, bannerHovered ? 0.38 : 0),
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer',
                            zIndex: 2,
                        }}
                    >
                        {uploading && cropTarget === 'banner' ? (
                            <CircularProgress size={28} sx={{ color: theme.palette.neutralWhite.main }} />
                        ) : bannerHovered ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    borderRadius: '10px',
                                    backgroundColor: alpha(theme.palette.neutralBlack.main, 0.55),
                                    border: `1px solid ${alpha(theme.palette.neutralWhite.main, 0.35)}`,
                                }}
                            >
                                <i className="fi fi-br-camera" style={{ fontSize: 16, color: theme.palette.neutralWhite.main }} />
                                <Typography variant="body2" sx={{ color: theme.palette.neutralWhite.main, fontWeight: 600 }}>
                                    Modifier la bannière
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        borderRadius: '14px',
                        border: `1px solid ${alpha(theme.palette.neutralWhite.main, 0.6)}`,
                        backgroundColor: alpha(theme.palette.neutralBlack.main, 0.2),
                        backdropFilter: 'blur(3px)',
                        zIndex: 3,
                    }}
                >
                    {/* Logo with edit overlay */}
                    <Box
                        onMouseEnter={() => editable && setLogoHovered(true)}
                        onMouseLeave={() => editable && setLogoHovered(false)}
                        onClick={(e) => { if (editable) { e.stopPropagation(); logoInputRef.current?.click(); } }}
                        sx={{
                            position: 'relative',
                            flexShrink: 0,
                            cursor: editable ? 'pointer' : 'default',
                            borderRadius: '50%',
                        }}
                    >
                        <Avatar
                            src={avatarSrc}
                            sx={{
                                width: 52,
                                height: 52,
                                color: theme.palette.neutralBlack.main,
                                backgroundColor: theme.palette.neutralWhite.main,
                                fontWeight: 700,
                            }}
                        >
                            {companyName.charAt(0).toUpperCase()}
                        </Avatar>
                        {editable && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: alpha(theme.palette.neutralBlack.main, logoHovered ? 0.5 : 0),
                                    transition: 'background-color 0.2s ease',
                                }}
                            >
                                {uploading && cropTarget === 'logo' ? (
                                    <CircularProgress size={18} sx={{ color: theme.palette.neutralWhite.main }} />
                                ) : logoHovered ? (
                                    <i className="fi fi-br-camera" style={{ fontSize: 14, color: theme.palette.neutralWhite.main }} />
                                ) : null}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                color: theme.palette.neutralWhite.main,
                                fontWeight: 700,
                                lineHeight: 1.2,
                                mb: 0.75,
                            }}
                        >
                            {companyName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {city && (
                                <Typography variant="body2" sx={{ color: theme.palette.neutralWhite.main, fontWeight: 700 }}>
                                    <i className="fi fi-br-marker" style={{ marginRight: 6, fontSize: 12 }} />
                                    {city}
                                </Typography>
                            )}
                            {category && (
                                <Typography variant="body2" sx={{ color: theme.palette.neutralWhite.main, fontWeight: 700 }}>
                                    <i className="fi fi-br-apps" style={{ marginRight: 6, fontSize: 12 }} />
                                    {category}
                                </Typography>
                            )}
                            {website && (
                                <Typography
                                    component={Link}
                                    href={website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.neutralWhite.main,
                                        fontWeight: 700,
                                        minWidth: 0,
                                    }}
                                >
                                    <i className="fi fi-br-link" style={{ marginRight: 6, fontSize: 12 }} />
                                    {website}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

{(pro?.salaryStats?.lowest != null || pro?.salaryStats?.highest != null) && (
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: '14px',
                        border: `1px solid ${alpha(theme.palette.neutralWhite.main, 0.6)}`,
                        backgroundColor: alpha(theme.palette.neutralBlack.main, 0.26),
                        color: theme.palette.neutralWhite.main,
                        zIndex: 3,
                    }}
                >
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1 }}>
                        Salaire garanti
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {pro?.salaryStats?.lowest != null && pro?.salaryStats?.highest != null
                                ? `${Math.round(pro.salaryStats.lowest / 1000)} - ${Math.round(pro.salaryStats.highest / 1000)} K€`
                                : pro?.salaryStats?.lowest != null
                                ? `${Math.round(pro.salaryStats.lowest / 1000)} K€`
                                : `${Math.round(pro.salaryStats.highest! / 1000)} K€`}
                        </Typography>
                        {pro?.numberOfEmployees != null && (
                            <Chip
                                label={pro.numberOfEmployees}
                                size="small"
                                icon={<i className="fi fi-br-user" style={{ fontSize: 11 }} />}
                                sx={{
                                    color: theme.palette.neutralWhite.main,
                                    bgcolor: alpha(theme.palette.primary.main, 0.9),
                                    '& .MuiChip-label': { fontWeight: 700 },
                                    '& .MuiChip-icon': { color: theme.palette.neutralWhite.main },
                                }}
                            />
                        )}
                    </Box>
                </Box>
)}
            </Box>

            {/* Crop dialog */}
            <Dialog open={!!imageSrc} onClose={handleCropCancel} maxWidth="md" fullWidth>
                <DialogTitle>
                    {cropTarget === 'banner' ? 'Recadrer la bannière' : 'Recadrer le logo'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
                        {imageSrc && (
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={cropTarget === 'banner' ? 16 / 9 : 1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        )}
                    </Box>
                    <Box sx={{ mt: 2, px: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                            Zoom
                        </Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            onChange={(_, value) => setZoom(value as number)}
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCropCancel} disabled={uploading} variant="text" color="secondary">Annuler</Button>
                    <Button onClick={handleCropSave} variant="contained" disabled={uploading}>
                        {uploading ? <CircularProgress size={18} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompanyBanner;
